import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { idtDesignEvaluationApi } from '../lib/api.js';
import type {
  EvaluationTask,
  SubmitEvaluationAnswerInput,
} from '../types/idt-design-evaluation.js';

/**
 * アンケートタスク一覧の query key。担当案件テーブルとタブのバッジが同じキーを共有し、
 * TanStack Query の dedup で 1 回のリクエストに畳まれる。
 * @graph-connects none
 */
const MY_TASKS_KEY = ['idt-design-evaluation', 'my-tasks'] as const;

/**
 * 自分が回答すべきアンケート一覧を取得するフック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が PL/主担当のアンケートタスク一覧を TanStack Query 経由で取得する
 * @graph-connects idtDesignEvaluationApi.getMyTasks [calls] アンケートタスク一覧 API
 */
export function useMyEvaluationTasks(enabled = true) {
  return useQuery({
    queryKey: MY_TASKS_KEY,
    queryFn: () => idtDesignEvaluationApi.getMyTasks(),
    staleTime: 60 * 1000,
    enabled,
  });
}

/**
 * アンケート回答を登録するミューテーションフック。
 *
 * 成功したら一覧を invalidate して「回答済」表示へ即座に切り替える。1 子チケット 1 回のみで
 * 編集できない仕様のため、楽観的更新はせずサーバの結果を待つ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アンケート回答を登録し、タスク一覧を再取得して回答済み表示へ切り替える
 * @graph-connects idtDesignEvaluationApi.submitAnswer [calls] アンケート回答登録 API
 */
export function useSubmitEvaluationAnswer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitEvaluationAnswerInput) => idtDesignEvaluationApi.submitAnswer(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MY_TASKS_KEY });
    },
  });
}

/**
 * アンケートタスクを**子チケットキー**で引けるようにする。
 * 子チケット 1 件につきアンケートは 1 通なので、値は配列ではなく単数。
 * デザイン view の子票行がこの索引で自分のアンケートを 1 件だけ取る。
 * @graph-connects none
 */
export function indexTasksByChild(tasks: EvaluationTask[]): Map<string, EvaluationTask> {
  return new Map(tasks.map((task) => [task.childIssueKey, task]));
}

/**
 * タスク一覧を母票キーでグループ化する。
 *
 * 担当案件テーブルは母票 1 行だが、1 母票に複数の子チケット（デザイン / 情報設計、
 * デザイナー違い）がぶら下がるため、行に出すには畳む必要がある。
 * @graph-connects none
 */
export function groupTasksByParent(tasks: EvaluationTask[]): Map<string, EvaluationTask[]> {
  const grouped = new Map<string, EvaluationTask[]>();
  for (const task of tasks) {
    const bucket = grouped.get(task.parentIssueKey);
    if (bucket) {
      bucket.push(task);
    } else {
      grouped.set(task.parentIssueKey, [task]);
    }
  }
  return grouped;
}
